/** Reusable building blocks used across SS-4.1 → 4.9 workspaces:
 *  - StageTimeline: visual funnel chip strip with completion state
 *  - QuickLinksPanel: lateral-nav across the 9 sub-modules
 *  - HowItWorks: collapsible "ultra-detailed how it works" accordion
 *  - SourcingAgentPanel: chat box bound to a per-module AI agent
 *  - StageAdvanceButton: pushes the event to the next stage
 */
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Compass, Users, MessageSquare, Send, Eye, Calculator, Gavel,
  Sparkles, Layers, ArrowRight, BookOpen, ChevronDown, ChevronUp,
  Bot, Loader2, Zap, CheckCircle2, Clock, Award, Trophy, Archive,
  Mail, BarChart3, Building2, ShieldCheck, Mic, MicOff, Volume2, VolumeX,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { AGENT_STARTERS } from '@/config/agentStarters';
import { useVoiceMode } from '@/hooks/useVoiceMode';
import { QuickLinksPanel as CanonicalQuickLinksPanel } from '@/components/QuickLinksPanel';
import { useSourcingRails, RailPortal } from '@/components/layout/SourcingRailedLayout';
import AgentActivityIndicator from '@/components/AgentActivityIndicator';
import { AgentMessage } from '@/components/agent/AgentMessage';

export const SS_MODULES = [
  { code: 'SS-4.1', key: 'PLANNING',      label: 'Strategy & Planning',  Icon: Compass,       route: '/app/sourcing/lifecycle', tone: 'sky' },
  { code: 'SS-4.2', key: 'INVITATION',    label: 'Supplier Invitation',  Icon: Users,         route: '/app/sourcing/supplier-invitation', tone: 'indigo' },
  { code: 'SS-4.3', key: 'CLARIFICATION', label: 'Clarification',        Icon: MessageSquare, route: '/app/sourcing/clarifications', tone: 'purple' },
  { code: 'SS-4.4', key: 'BIDDING',       label: 'Bid Submission',       Icon: Send,          route: '/app/sourcing/bid-submission', tone: 'fuchsia' },
  { code: 'SS-4.5', key: 'OPENING',       label: 'Bid Opening',          Icon: Eye,           route: '/app/sourcing/bid-opening', tone: 'rose' },
  { code: 'SS-4.6', key: 'EVALUATION',    label: 'Evaluation',           Icon: Calculator,    route: '/app/sourcing/evaluation', tone: 'amber' },
  { code: 'SS-4.7', key: 'AUCTION',       label: 'eAuction',             Icon: Gavel,         route: '/app/sourcing/auction', tone: 'teal' },
  { code: 'SS-4.8', key: 'BAFO',          label: 'BAFO',                 Icon: Sparkles,      route: '/app/sourcing/bafo', tone: 'violet' },
  { code: 'SS-4.9', key: 'FRAMEWORK',     label: 'Framework Agreement',  Icon: Layers,        route: '/app/sourcing/framework-agreements', tone: 'emerald' },
  { code: 'SS-4.10', key: 'DIRECT_AWARD',  label: 'Direct Award',         Icon: Award,         route: '/app/sourcing/direct-awards', tone: 'violet' },
  { code: 'SS-4.11', key: 'RECOMMENDATION', label: 'Award Recommendation', Icon: Trophy,        route: '/app/sourcing/award-recommendation', tone: 'amber' },
  { code: 'SS-4.12', key: 'CLOSURE',        label: 'Award & Closure',      Icon: Archive,       route: '/app/sourcing/award-closure', tone: 'emerald' },
  { code: 'SS-4.13', key: 'COMMUNICATION',  label: 'Communication Engine', Icon: Mail,          route: '/app/sourcing/communication-engine', tone: 'sky' },
  { code: 'SS-4.14', key: 'ANALYTICS',      label: 'Analytics & Reporting',Icon: BarChart3,     route: '/app/sourcing/analytics', tone: 'indigo' },
  { code: 'SS-4.15', key: 'PORTAL',         label: 'Supplier Portal',      Icon: Building2,     route: '/app/sourcing/portal-admin', tone: 'teal' },
  { code: 'SS-4.16', key: 'COMPLIANCE',     label: 'ITT Compliance',       Icon: ShieldCheck,   route: '/app/sourcing/itt-compliance', tone: 'rose' },
];

export function StageTimeline({ currentStage, eventId, compact = false }) {
  const navigate = useNavigate();
  const idx = SS_MODULES.findIndex(m => m.key === currentStage);
  return (
    <div className="flex items-center gap-0 overflow-x-auto py-1" data-testid="stage-timeline">
      {SS_MODULES.map((m, i) => {
        const done = i < idx;
        const cur = i === idx;
        const Icon = m.Icon;
        const target = eventId ? `${m.route}${m.route.endsWith('/') ? '' : '/'}${eventId}` : m.route;
        return (
          <button key={m.key} onClick={() => navigate(target)}
                  className={`group relative flex items-center gap-1 px-2 py-1 ${compact ? 'text-[10px]' : 'text-xs'} whitespace-nowrap transition`}
                  data-testid={`stage-chip-${m.key.toLowerCase()}`}>
            <span className={`flex items-center justify-center w-5 h-5 rounded-full
              ${cur ? `bg-${m.tone}-600 text-white ring-2 ring-${m.tone}-200` :
                done ? 'bg-emerald-100 text-emerald-700' :
                'bg-gray-100 text-gray-400'}`}>
              {done ? <CheckCircle2 className="w-3 h-3"/> : cur ? <Icon className="w-3 h-3"/> : <Clock className="w-3 h-3"/>}
            </span>
            <span className={`${cur ? 'font-semibold text-gray-900' : done ? 'text-emerald-700' : 'text-gray-400'}`}>{m.code}</span>
            {i < SS_MODULES.length - 1 && <span className={`w-3 h-px ${done ? 'bg-emerald-400' : 'bg-gray-200'}`}/>}
          </button>
        );
      })}
    </div>
  );
}

/* DEPRECATED iter-127: icon-tile sourcing QL row replaced by canonical green
   QuickLinksPanel (Internal + External) auto-rendered via ModulePageShell. */
export function QuickLinksPanel() {
  return null;
}

/* DEPRECATED iter-127: HowItWorks chip removed (duplicate of Quick Links + ⌘K). */
/* RESTORED iter-132: per-stage ultra-detailed walkthrough accordion. */
export function HowItWorks({ moduleKey, sections, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!sections || sections.length === 0) return null;
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/30 mb-3"
         data-testid={`how-it-works-${(moduleKey || '').toLowerCase()}`}>
      <button onClick={() => setOpen(!open)}
              className="w-full flex items-center justify-between p-2.5 text-left"
              data-testid={`how-it-works-toggle-${(moduleKey || '').toLowerCase()}`}>
        <div className="flex items-center gap-2 text-xs font-semibold text-sky-900">
          <BookOpen className="w-3.5 h-3.5"/> How it works — ultra-detailed walkthrough
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-sky-700"/> : <ChevronDown className="w-3.5 h-3.5 text-sky-700"/>}
      </button>
      {open && (
        <div className="p-2.5 pt-0 space-y-2 text-[11.5px] text-sky-900/85">
          {sections.map((s, i) => (
            <div key={i} className="rounded bg-white border border-sky-100 p-2.5"
                 data-testid={`how-section-${i}`}>
              <div className="font-semibold text-[12px] text-sky-950 mb-1 flex items-center gap-1.5">
                <span className="bg-sky-100 text-sky-700 rounded px-1.5 py-0.5 text-[10px] font-mono">{i+1}</span>
                {s.title}
              </div>
              <div className="text-[11px] leading-relaxed">{s.body}</div>
              {s.steps && (
                <ol className="list-decimal list-inside mt-1.5 text-[11px] space-y-0.5 text-sky-800/90">
                  {s.steps.map((step, j) => <li key={j}>{step}</li>)}
                </ol>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SourcingAgentPanel({ moduleKey, agentName, contextData, defaultOpen = false, neighbors = [] }) {
  const [open, setOpen] = useState(defaultOpen);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showHandoff, setShowHandoff] = useState(false);
  const navigate = useNavigate();
  const [voiceOverride, setVoiceOverride] = useState(
    () => localStorage.getItem(`voice_override_${moduleKey}`) || ''
  );
  const [voiceCatalog, setVoiceCatalog] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs.length]);
  useEffect(() => {
    if (!voiceCatalog) api.get('/voice/voices').then(r => setVoiceCatalog(r.data)).catch(() => {});
  }, [voiceCatalog]);

  // ── Cross-Agent Handoff: on mount, check sessionStorage for a payload
  //    addressed to this moduleKey and seed the conversation with the
  //    referring agent's last user message + a courteous handoff prefix.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(`cognara_handoff_${moduleKey}`);
      if (!raw) return;
      const payload = JSON.parse(raw);
      sessionStorage.removeItem(`cognara_handoff_${moduleKey}`);
      const carry = (payload?.lastInput || '').trim();
      const fromLabel = payload?.fromLabel || payload?.from || 'a colleague';
      if (carry) {
        setInput(`(Handed off from ${fromLabel}) ${carry}`);
      }
      setOpen(true);
      setMsgs([{ role: 'agent', text: `👋 Picking up from ${fromLabel}. I can see the prior context — ask away.`, fallback: false }]);
    } catch (e) { /* ignore malformed handoff payload */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handOffTo = (neighbor) => {
    try {
      sessionStorage.setItem(`cognara_handoff_${neighbor.key}`, JSON.stringify({
        from: moduleKey,
        fromLabel: agentName,
        lastInput: input || (msgs.filter(m => m.role === 'user').slice(-1)[0]?.text ?? ''),
        contextData: contextData || null,
        handedAt: new Date().toISOString(),
      }));
    } catch (e) { /* sessionStorage full or disabled */ }
    setShowHandoff(false);
    if (neighbor.location) navigate(neighbor.location);
  };

  const sendMessage = async (q) => {
    const text = (q || '').trim(); if (!text) return;
    setMsgs(m => [...m, { role: 'user', text }]);
    setInput(''); setBusy(true);
    try {
      const r = await api.post(`/v1/sourcing/agents/${moduleKey}/chat`,
        { message: text, context: contextData, sessionId });
      if (!sessionId) setSessionId(r.data.sessionId);
      setMsgs(m => [...m, { role: 'agent', text: r.data.reply, fallback: r.data.fallback }]);
    } catch (e) {
      setMsgs(m => [...m, { role: 'agent', text: '⚠ Agent unavailable. Try again shortly.', fallback: true }]);
    } finally { setBusy(false); }
  };
  const send = () => sendMessage(input);

  const starters = AGENT_STARTERS[moduleKey] || [];

  // Voice mode (Whisper STT + OpenAI TTS) — per-agent voice persona
  const { recording, recStartStop, voiceOn, toggleVoice, ttsBusy, playReply } =
    useVoiceMode({
      agent: moduleKey,
      voiceOverride,
      onTranscript: (t) => setInput((cur) => (cur ? cur + ' ' : '') + t),
    });

  const onVoiceChange = (v) => {
    setVoiceOverride(v);
    if (v) localStorage.setItem(`voice_override_${moduleKey}`, v);
    else localStorage.removeItem(`voice_override_${moduleKey}`);
  };

  const playSample = async () => {
    setPreviewBusy(true);
    try {
      const r = await api.post('/voice/tts', {
        text: `Hi, I'm the ${agentName}. Ready when you are.`,
        agent: moduleKey,
        voice: voiceOverride || undefined,
      });
      const audio = new Audio(r.data.audio_url);
      audio.onended = () => setPreviewBusy(false);
      audio.onerror = () => setPreviewBusy(false);
      await audio.play();
    } catch (e) {
      setPreviewBusy(false);
    }
  };

  const assignedVoice = voiceOverride
    || (voiceCatalog?.agents?.[moduleKey])
    || voiceCatalog?.default
    || 'onyx';

  // Auto-speak agent replies when voice is on
  useEffect(() => {
    const last = msgs[msgs.length - 1];
    if (last && last.role === 'agent' && !last.fallback && voiceOn) {
      playReply(last.text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgs.length, voiceOn]);

  return (
    <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 mb-4 overflow-hidden" data-testid={`agent-panel-${moduleKey}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-3 text-left"
              data-testid={`agent-toggle-${moduleKey}`}>
        <div className="flex items-center gap-2 text-sm font-semibold text-violet-900 flex-wrap">
          <Bot className="w-4 h-4"/> AI Agent — {agentName}
          {/* iter-227 — removed the placeholder "Cognara / Idle" chips
              that made every specialist look like the same generic
              badge. The specialist name + activity indicator below
              already convey what the agent is. */}
          {contextData?.stage && (
            <span className="text-[9px] bg-white text-violet-700 border border-violet-300 px-1.5 py-0.5 rounded font-semibold" data-testid={`agent-stage-${moduleKey}`}>
              {contextData.stage}
            </span>
          )}
          {contextData?.subTab && (
            <span className="text-[9px] bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-300 px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide" data-testid={`agent-subtab-${moduleKey}`}>
              {String(contextData.subTab)}
            </span>
          )}
          {voiceOn && (
            <span className="text-[9px] bg-emerald-100 text-emerald-700 border border-emerald-300 px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5">
              <Volume2 className="w-2.5 h-2.5"/> Voice on
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-violet-700"/> : <ChevronDown className="w-4 h-4 text-violet-700"/>}
      </button>
      {/* Iter-163 — live AI activity indicator */}
      <div className="px-3 -mt-1 pb-2 border-b border-violet-100">
        <AgentActivityIndicator moduleKey={moduleKey} />
      </div>
      {open && (
        <div className="p-3 pt-0">
          {neighbors.length > 0 && (
            <div className="mb-2 relative" data-testid={`agent-handoff-${moduleKey}`}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowHandoff(!showHandoff); }}
                className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-violet-300 bg-white/80 hover:bg-violet-100 text-violet-700 font-semibold"
                data-testid={`agent-handoff-toggle-${moduleKey}`}
                title="Refer this thread to a closest-cousin Cognara specialist">
                <ArrowRight className="w-3 h-3" /> Hand off to specialist
                <ChevronDown className="w-3 h-3 opacity-60" />
              </button>
              {showHandoff && (
                <div className="absolute z-30 mt-1 w-72 bg-white border border-violet-300 rounded-lg shadow-lg p-1" data-testid={`agent-handoff-menu-${moduleKey}`}>
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-violet-700/80 font-semibold border-b border-violet-100">
                    Cross-Agent Handoff
                  </div>
                  {neighbors.map((n) => (
                    <button
                      key={n.key}
                      onClick={(e) => { e.stopPropagation(); handOffTo(n); }}
                      className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-violet-50 rounded flex flex-col gap-0.5"
                      data-testid={`agent-handoff-option-${moduleKey}-${n.key}`}>
                      <span className="font-semibold text-violet-900">{n.name}</span>
                      <span className="text-[9px] text-violet-600">{n.label} · carries your last question forward</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {starters.length > 0 && msgs.length === 0 && (
            <div className="mb-2" data-testid={`agent-starters-${moduleKey}`}>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700/80 mb-1.5 flex items-center gap-1">
                <Sparkles className="w-3 h-3"/> What can I ask?
              </div>
              <div className="flex flex-wrap gap-1.5">
                {starters.map((s, i) => (
                  <button key={i}
                    onClick={() => sendMessage(s)}
                    disabled={busy}
                    className="text-left text-[11px] px-2 py-1 rounded-full border border-violet-300 bg-white/80 hover:bg-violet-100 hover:border-violet-500 transition disabled:opacity-50"
                    data-testid={`agent-starter-${moduleKey}-${i}`}
                    title="Click to ask this">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="bg-white/70 rounded-lg p-2 max-h-72 overflow-y-auto mb-2" data-testid={`agent-msgs-${moduleKey}`}>
            {msgs.length === 0 && (
              <div className="text-[11px] text-violet-700/70 italic">Ask anything about {agentName.toLowerCase()}'s scope — e.g., "What's the recommended evaluation methodology for a high-value services RFP?"</div>
            )}
            {msgs.map((m, i) => (
              <AgentMessage key={i} role={m.role} text={m.text} fallback={m.fallback} />
            ))}
            {busy && <div className="flex items-center gap-1 text-[11px] text-violet-700"><Loader2 className="w-3 h-3 animate-spin"/> Thinking…</div>}
            <div ref={bottomRef}/>
          </div>
          {voiceCatalog && (
            <div className="flex items-center gap-1.5 mb-2 text-[10px]" data-testid={`agent-voice-picker-${moduleKey}`}>
              <Volume2 className="w-3 h-3 text-violet-700"/>
              <span className="text-violet-700/80 font-semibold uppercase tracking-wider">Voice</span>
              <select
                value={voiceOverride}
                onChange={(e) => onVoiceChange(e.target.value)}
                className="bg-white border border-violet-200 rounded px-1.5 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                data-testid={`agent-voice-select-${moduleKey}`}>
                <option value="">{`Default · ${voiceCatalog.agents?.[moduleKey] || voiceCatalog.default}`}</option>
                {(voiceCatalog.available || []).map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
              <button
                onClick={playSample}
                disabled={previewBusy}
                className="px-1.5 py-0.5 rounded border border-violet-200 bg-white hover:bg-violet-100 text-[11px] flex items-center gap-1 disabled:opacity-50"
                data-testid={`agent-voice-preview-${moduleKey}`}
                title={`Hear "${assignedVoice}" sample`}>
                {previewBusy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Volume2 className="w-3 h-3"/>}
                Preview
              </button>
              {voiceOverride && (
                <button onClick={() => onVoiceChange('')}
                  className="text-[10px] text-violet-700 hover:underline"
                  data-testid={`agent-voice-reset-${moduleKey}`}>reset</button>
              )}
            </div>
          )}
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => { e.preventDefault(); recStartStop(); }}
              disabled={busy}
              className={`h-9 w-9 rounded flex items-center justify-center transition
                ${recording ? 'bg-rose-600 text-white animate-pulse'
                            : 'bg-white border border-violet-300 text-violet-700 hover:bg-violet-100'}`}
              title={recording ? 'Click to stop recording' : 'Hold-free dictation: click to start, click to stop'}
              data-testid={`agent-mic-${moduleKey}`}>
              {recording ? <MicOff className="w-4 h-4"/> : <Mic className="w-4 h-4"/>}
            </button>
            <button
              onClick={(e) => { e.preventDefault(); toggleVoice(); }}
              className={`h-9 w-9 rounded flex items-center justify-center transition
                ${voiceOn ? 'bg-emerald-600 text-white'
                          : 'bg-white border border-violet-300 text-violet-700 hover:bg-violet-100'}`}
              title={voiceOn ? 'Voice playback ON — click to mute' : 'Voice playback OFF — click to enable'}
              data-testid={`agent-voice-toggle-${moduleKey}`}>
              {voiceOn ? <Volume2 className="w-4 h-4"/> : <VolumeX className="w-4 h-4"/>}
              {ttsBusy && voiceOn && <Loader2 className="w-3 h-3 ml-0.5 animate-spin"/>}
            </button>
            <input value={input} onChange={e => setInput(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && send()}
                   placeholder={recording ? 'Recording… speak now' : `Ask the ${agentName}…`}
                   className="flex-1 h-9 text-xs px-2 rounded border border-violet-200 focus:outline-none focus:ring-2 focus:ring-violet-400"
                   data-testid={`agent-input-${moduleKey}`}/>
            <Button onClick={send} disabled={busy} size="sm" className="h-9 bg-violet-600 hover:bg-violet-700" data-testid={`agent-send-${moduleKey}`}>
              <Send className="w-3 h-3"/>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function StageAdvanceButton({ eventId, currentStage, label, className = '', testid = 'advance-stage-btn' }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const nextIdx = SS_MODULES.findIndex(m => m.key === currentStage) + 1;
  const next = SS_MODULES[nextIdx];
  if (!next) return null;
  const onClick = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/v1/sourcing/events/${eventId}/advance`, {});
      toast.success(`Advanced to ${r.data.toStage}`);
      navigate(`${r.data.nextRoute}/${eventId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Advance failed');
    } finally { setBusy(false); }
  };
  return (
    <Button onClick={onClick} disabled={busy} className={`bg-gradient-to-r from-${next.tone}-600 to-${next.tone}-700 hover:opacity-90 text-white ${className}`} data-testid={testid}>
      {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <ArrowRight className="w-4 h-4 mr-1"/>}
      {label || `Continue to ${next.label}`}
    </Button>
  );
}

/**
 * Maps the legacy uppercase SourcingL2Header moduleKey to the canonical
 * QuickLinksPanel registry key in /config/moduleLinks.js.
 */
const SS_MODULE_TO_QLP_KEY = {
  STRATEGY: 'sourcing-strategic',
  PLANNING: 'sourcing-events',  // SS-4.1 hub
  INVITATION: 'supplier-invitation',
  CLARIFICATION: 'sourcing-strategic',
  SUBMISSION: 'bid-submission',
  BIDDING: 'bid-submission',
  OPENING: 'bid-submission',
  EVALUATION: 'evaluation',
  AUCTION: 'auction',
  BAFO: 'bafo',
  FRAMEWORK: 'framework',
  DIRECTAWARD: 'direct-award',
  DIRECT_AWARD: 'direct-award',
  RECOMMENDATION: 'award-recommendation',
  CLOSURE: 'award-recommendation',
  COMMUNICATION: 'communication-engine',
  ANALYTICS: 'sourcing-analytics',
  PORTAL: 'supplier-portal-admin',
  COMPLIANCE: 'itt-compliance',
};

/**
 * One-stop assembled header for any L2 workspace.
 *
 * iter-134: When wrapped in `<SourcingRailedLayout>`, the QL card + HIW
 * accordion teleport to the left rail and the AI agent panel teleports to
 * the right rail. When NOT wrapped, falls back to the legacy stacked render
 * (so no SS page breaks).
 */
export function SourcingL2Header({ moduleKey, agentModule, agentName, howItWorks, contextData }) {
  const qlpKey = SS_MODULE_TO_QLP_KEY[moduleKey];
  const rails = useSourcingRails();
  const QL = qlpKey ? <CanonicalQuickLinksPanel moduleKey={qlpKey}/> : null;
  const HIW = <HowItWorks moduleKey={moduleKey} sections={howItWorks}/>;
  const Agent = <SourcingAgentPanel moduleKey={agentModule} agentName={agentName} contextData={contextData}/>;
  if (rails && rails.ready) {
    return (
      <>
        <RailPortal side="left">{QL}{HIW}</RailPortal>
        <RailPortal side="right">{Agent}</RailPortal>
      </>
    );
  }
  return (
    <>
      {QL}
      {HIW}
      {Agent}
    </>
  );
}
